import { useState } from 'react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import { cn, formatTimestamp, getSeverityColor, getSeverityBg, getStatusColor, getCategoryIcon } from '../../lib/utils';
import type { Incident, IncidentStatus, IncidentSeverity } from '../../types';

type SortKey = 'id' | 'severity' | 'status' | 'reportedAt' | 'title';
type SortDir = 'asc' | 'desc';

const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

interface IncidentTableProps {
  incidents: Incident[];
  onSelect?: (incident: Incident) => void;
  selectedId?: string;
  compact?: boolean;
  className?: string;
  showFilters?: boolean;
}

export default function IncidentTable({
  incidents,
  onSelect,
  selectedId,
  compact = false,
  className,
  showFilters = true,
}: IncidentTableProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | 'all' | 'active'>('active');
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('reportedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = incidents
    .filter((i) => statusFilter === 'all' || (statusFilter === 'active' ? i.status !== 'closed' : i.status === statusFilter))
    .filter((i) => severityFilter === 'all' || i.severity === severityFilter)
    .filter((i) =>
      !search || i.title.toLowerCase().includes(search.toLowerCase()) ||
      i.id.toLowerCase().includes(search.toLowerCase()) ||
      (i.location.address || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'severity') return (severityOrder[a.severity] - severityOrder[b.severity]) * dir;
      if (sortKey === 'reportedAt') return (new Date(a.reportedAt).getTime() - new Date(b.reportedAt).getTime()) * dir;
      const aVal = String((a as unknown as Record<string, unknown>)[sortKey] || '');
      const bVal = String((b as unknown as Record<string, unknown>)[sortKey] || '');
      return aVal.localeCompare(bVal) * dir;
    });

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className={cn('flex flex-col min-h-0', className)}>
      {showFilters && (
        <div className="flex items-center gap-2 p-2 border-b border-border flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search incidents..."
              className="w-full h-7 pl-7 pr-2 text-xs bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as IncidentStatus | 'all' | 'active')}
            className="h-7 px-2 text-xs bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="active">Active</option>
            <option value="supervision">Supervision</option>
            <option value="closed">Closed</option>
            <option value="all">All Incidents</option>
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as IncidentSeverity | 'all')}
            className="h-7 px-2 text-xs bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b border-border">
              <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('id')}>
                <span className="flex items-center gap-1">ID <SortIcon col="id" /></span>
              </th>
              <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('severity')}>
                <span className="flex items-center gap-1">Sev <SortIcon col="severity" /></span>
              </th>
              <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('title')}>
                <span className="flex items-center gap-1">Incident <SortIcon col="title" /></span>
              </th>
              {!compact && (
                <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Location</th>
              )}
              <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('status')}>
                <span className="flex items-center gap-1">Status <SortIcon col="status" /></span>
              </th>
              <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('reportedAt')}>
                <span className="flex items-center gap-1">Time <SortIcon col="reportedAt" /></span>
              </th>
              {!compact && (
                <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Units</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((inc) => (
              <tr
                key={inc.id}
                onClick={() => onSelect?.(inc)}
                className={cn(
                  'cursor-pointer hover:bg-accent/50 transition-colors',
                  selectedId === inc.id && 'bg-primary/10'
                )}
              >
                <td className="px-2 py-1.5 font-mono text-muted-foreground whitespace-nowrap">{inc.id}</td>
                <td className="px-2 py-1.5">
                  <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border', getSeverityBg(inc.severity), getSeverityColor(inc.severity))}>
                    {inc.severity}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <span>{getCategoryIcon(inc.category)}</span>
                    <span className="font-medium text-foreground truncate max-w-[200px]">{inc.title}</span>
                  </div>
                </td>
                {!compact && (
                  <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[180px]">{inc.location.address || '—'}</td>
                )}
                <td className="px-2 py-1.5">
                  <span className={cn('font-medium capitalize', getStatusColor(inc.status))}>
                    {inc.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono text-muted-foreground whitespace-nowrap">{formatTimestamp(inc.reportedAt)}</td>
                {!compact && (
                  <td className="px-2 py-1.5 text-muted-foreground">{inc.assignedUnits.length}</td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No incidents found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
